import { useState, useCallback, useEffect, useRef } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import PageHeader from '../../components/PageHeader';
import ToastNotification from '../../components/ToastNotification';
import styles from './HelpPage.module.css';

const INITIAL_FAQS = [
  {
    id: 'FAQ-001',
    question: 'How do I place a supply order?',
    answer: 'Navigate to Orders in the sidebar, browse available products, add them to your cart, and click Place Order.',
  },
  {
    id: 'FAQ-002',
    question: 'What payment methods are available?',
    answer: 'Two modes: Cash on Delivery (full payment on receipt) and Partial Payment (pay a portion upon arrival of goods; admin sets a due date for the remaining balance).',
  },
  {
    id: 'FAQ-003',
    question: 'How does the WFQ algorithm prioritize orders?',
    answer: 'Orders are weighted by demand, stock urgency, and order age. Higher-weight orders are processed first by the supplier.',
  },
  {
    id: 'FAQ-004',
    question: 'How do I track my delivery?',
    answer: 'Go to the Tracking module in the sidebar to see your order status and estimated arrival time.',
  },
  {
    id: 'FAQ-005',
    question: 'How do I report a system issue?',
    answer: 'Use the Report module to submit a ticket. The admin will be notified and respond as soon as possible.',
  },
];


function FAQModal({ initial, onSave, onCancel }) {
  const [question, setQuestion] = useState(initial?.question || '');
  const [answer,   setAnswer]   = useState(initial?.answer   || '');
  const isEdit = !!initial;

  function handleSubmit(e) {
    e.preventDefault();
    if (!question.trim() || !answer.trim()) return;
    onSave({ question: question.trim(), answer: answer.trim() });
  }

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>{isEdit ? 'Edit FAQ' : 'Add FAQ'}</h3>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Question</span>
            <input
              className={styles.input}
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="Enter FAQ question"
              required
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Answer</span>
            <textarea
              className={`${styles.input} ${styles.textarea}`}
              value={answer}
              onChange={e => setAnswer(e.target.value)}
              placeholder="Enter answer..."
              rows={4}
              required
            />
          </label>
          <div className={styles.modalActions}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
            <button type="submit" className={styles.saveBtn}>{isEdit ? 'Save Changes' : 'Add FAQ'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


export default function HelpPage() {
  const [faqs,       setFaqs]       = useState([]);
  const [openId,     setOpenId]     = useState(null);
  const [modalMode,  setModalMode]  = useState(null);
  const [toast,      setToast]      = useState(null);
  const dismissToast                = useCallback(() => setToast(null), []);
  const seeded                      = useRef(false);

  // Load FAQs from Firestore; seed INITIAL_FAQS if collection is empty
  useEffect(() => {
    const q = query(collection(db, 'faqs'), orderBy('createdAt', 'asc'));
    return onSnapshot(q, snap => {
      if (!snap.empty) {
        setFaqs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } else if (!seeded.current) {
        seeded.current = true;
        INITIAL_FAQS.forEach(faq => {
          addDoc(collection(db, 'faqs'), {
            question: faq.question,
            answer: faq.answer,
            createdAt: serverTimestamp(),
          }).catch(console.error);
        });
      }
    });
  }, []);

  function toggleFAQ(id) {
    setOpenId(prev => prev === id ? null : id);
  }

  async function handleSave(data) {
    try {
      if (modalMode === 'add') {
        await addDoc(collection(db, 'faqs'), { ...data, createdAt: serverTimestamp() });
        setToast({ message: 'FAQ added.', type: 'success' });
      } else {
        await updateDoc(doc(db, 'faqs', modalMode.id), { question: data.question, answer: data.answer });
        setToast({ message: 'FAQ updated.', type: 'success' });
      }
    } catch (e) {
      console.error('FAQ save failed:', e);
      setToast({ message: 'Failed to save FAQ.', type: 'error' });
    }
    setModalMode(null);
  }

  async function handleDelete(id) {
    try {
      await deleteDoc(doc(db, 'faqs', id));
      if (openId === id) setOpenId(null);
      setToast({ message: 'FAQ deleted.', type: 'success' });
    } catch (e) {
      console.error('FAQ delete failed:', e);
      setToast({ message: 'Failed to delete FAQ.', type: 'error' });
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Help Database"
        description="Manage FAQs and help content for users"
      />

      <div className={styles.toolbar}>
        <button className={styles.addBtn} onClick={() => setModalMode('add')}>+ Add FAQ</button>
      </div>

      <div className={styles.faqList}>
        {faqs.length === 0 && (
          <p className={styles.empty}>No FAQs yet. Click "+ Add FAQ" to create one.</p>
        )}
        {faqs.map(faq => (
          <div key={faq.id} className={styles.faqItem}>
            <button
              className={styles.faqHeader}
              onClick={() => toggleFAQ(faq.id)}
              aria-expanded={openId === faq.id}
            >
              <span className={styles.faqQuestion}>{faq.question}</span>
              <span className={styles.faqChevron}>{openId === faq.id ? '▲' : '▼'}</span>
            </button>
            {openId === faq.id && (
              <div className={styles.faqBody}>
                <p className={styles.faqAnswer}>{faq.answer}</p>
                <div className={styles.faqActions}>
                  <button className={styles.editBtn} onClick={() => setModalMode(faq)}>Edit</button>
                  <button className={styles.deleteBtn} onClick={() => handleDelete(faq.id)}>Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {modalMode && (
        <FAQModal
          initial={modalMode === 'add' ? null : modalMode}
          onSave={handleSave}
          onCancel={() => setModalMode(null)}
        />
      )}

      {toast && (
        <ToastNotification message={toast.message} type={toast.type} onDismiss={dismissToast} />
      )}
    </div>
  );
}
